import { useMemo, useState, type FC } from "react";
import { toJalaali } from "jalaali-js";
import Icon from "@/components/icon";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import DatePickerContent from "./date-picker-content";

type Props = {
  value?: string | null; // YYYY-MM-DD (Gregorian) or YYYY-MM-DD HH:MM:SS
  onSubmit: (val: string) => void;
  name: string;
  placeholder?: string;
  maxFutureYears?: number;
  inputClassName?: string;
  /** Trailing icon (sprite id). Defaults to a calendar. */
  iconName?: string;
};

const JALALI_MONTHS = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

// Convert "YYYY-MM-DD" Gregorian → "DD MONTH YYYY" Jalali for display.
function formatJalaliDate(isoDate: string) {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return "";
  const { jy, jm, jd } = toJalaali(y, m, d);
  return `${jd} ${JALALI_MONTHS[jm - 1]} ${jy}`;
}

const defaultInputClass =
  "w-full h-11 rounded-lg border border-default-100 bg-background px-3 text-sm text-default-800 placeholder:text-default-400 text-right flex items-center justify-between";

const DatePickerDrawer: FC<Props> = ({
  value,
  onSubmit,
  name,
  placeholder = "تاریخ را انتخاب کنید",
  maxFutureYears,
  inputClassName,
  iconName = "calendar",
}) => {
  const [open, setOpen] = useState(false);
  const normalizedValue = value ?? "";

  const date = useMemo(() => {
    if (!normalizedValue) return null;
    const datePart = normalizedValue.split(" ")[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
  }, [normalizedValue]);

  const displayDate = useMemo(
    () => (date ? formatJalaliDate(date) : ""),
    [date],
  );

  return (
    <Drawer open={open} onOpenChange={setOpen} handleOnly>
      <DrawerTrigger asChild>
        <button
          type="button"
          name={name}
          className={inputClassName ?? defaultInputClass}
        >
          <span className={displayDate ? "text-default-800" : "text-default-400"}>
            {displayDate || placeholder}
          </span>
          <Icon name={iconName} size={18} className="text-default-400" />
        </button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerTitle className="sr-only">انتخاب تاریخ</DrawerTitle>
        <DatePickerContent
          value={date}
          onSubmit={(val) => {
            onSubmit(val);
            setOpen(false);
          }}
          setOpen={setOpen}
          maxFutureYears={maxFutureYears}
        />
      </DrawerContent>
    </Drawer>
  );
};

export default DatePickerDrawer;
